import type { DraftFinding, LayoutDraft, LayoutSkeleton } from '@/api.ts';

/**
 * The layout editor's state, and every rule about it, as pure functions.
 *
 * The page is a textarea and a debounce; what makes it correct is which server answer is allowed
 * to touch the screen and when the findings are allowed to show. Both are decided here, where a
 * test can decide them too, rather than in effect handlers where the only test is timing.
 */
export interface EditorState {
  /** What the operator sees. The only thing the console owns. */
  text: string;
  /** What the server last said about some text — see `checked` for whether it was this text. */
  findings: DraftFinding[];
  skeleton: LayoutSkeleton | null;
  /** Whether `findings` and `skeleton` describe the current `text`. */
  checked: boolean;
  /** The saved draft's revision, or `null` when the shell has no layout yet. */
  revision: string | null;
  /** The text as last saved — what Publish would publish. `null` until something is saved. */
  savedText: string | null;
  /**
   * Whether findings may be shown. They compute on every change and appear after the first save
   * attempt — the `shell-config.ts` rule. A loaded draft has already survived a save, so its
   * findings show at once: they are the ones an operator came back to fix.
   */
  submitted: boolean;
}

/** Wait this long after the last keystroke before asking the server. */
export const DEBOUNCE_MS = 500;
/** Give up on a check after this long; the local text is untouched and the page says so. */
export const TIMEOUT_MS = 5000;

/** Why the live tier is refused. Present and disabled rather than absent, so it reads as a decision. */
export const LIVE_TIER_REASON =
  'Not wired yet. Rendering through the dataplane needs it to serve a compiled draft, and it cannot yet — the skeleton is what exists today.';

/** The editor for a shell, from what `GET /layout/draft` returned. */
export function openEditor(draft: LayoutDraft | null): EditorState {
  if (!draft) {
    return { text: '', findings: [], skeleton: null, checked: true, revision: null, savedText: null, submitted: false };
  }
  return {
    text: draft.text,
    findings: draft.findings,
    skeleton: draft.skeleton,
    checked: true,
    revision: draft.revision,
    savedText: draft.text,
    submitted: true,
  };
}

/**
 * The text changed — by a keystroke, a preset, or a fix; the editor cannot tell and does not need
 * to. The last findings stay on screen, marked stale, until the server describes the new text.
 */
export function edit(state: EditorState, text: string): EditorState {
  if (text === state.text) return state;
  return { ...state, text, checked: false };
}

/**
 * A description arrived. Applied only if it describes the text the operator has *now*.
 *
 * The page aborts a request when a newer keystroke lands, but an abort is advisory — the response
 * may already be on its way — and a slow answer to old text overwriting the findings for new text
 * is the bug this rule exists for. The text is the identity of the request, so the check needs no
 * sequence numbers and no clock.
 */
export function describe(state: EditorState, draft: LayoutDraft): EditorState {
  if (draft.text !== state.text) return state;
  return { ...state, findings: draft.findings, skeleton: draft.skeleton, checked: true };
}

/**
 * A save went through. The saved text is what was posted; the text on screen may already be newer,
 * because the textarea does not lock while a save is in flight — so the description is applied
 * through `describe`, which declines it if the operator has moved on.
 */
export function saved(state: EditorState, draft: LayoutDraft): EditorState {
  return { ...describe(state, draft), revision: draft.revision, savedText: draft.text, submitted: true };
}

/**
 * A save was refused with findings. The attempt is what turns them visible. `refusedText` is what
 * was posted; the findings describe the current text only if nothing was typed meanwhile.
 */
export function refused(state: EditorState, findings: DraftFinding[], refusedText: string): EditorState {
  return { ...state, findings, checked: refusedText === state.text, submitted: true };
}

/** The findings the page may draw right now. */
export function shownFindings(state: EditorState): DraftFinding[] {
  return state.submitted ? state.findings : [];
}

export function hasErrors(findings: readonly DraftFinding[]): boolean {
  return findings.some((finding) => finding.severity === 'error');
}

/** Whether the text differs from what is saved. Unsaved-and-never-saved counts. */
export function isDirty(state: EditorState): boolean {
  return state.text !== state.savedText;
}

/** Why Save is disabled, or `null`. Role refusals are the caller's; these are the document's. */
export function saveRefusal(state: EditorState): string | null {
  if (state.text.trim() === '') return 'Nothing to save yet. Start from a preset, or paste a document.';
  if (!isDirty(state)) return 'Saved. There is nothing new to save.';
  return null;
}

/**
 * Why Publish is disabled, or `null`.
 *
 * Publishing takes the *saved* draft (ADR 0007), so unsaved text would publish something other
 * than what is on screen — the refusal names that rather than letting it happen.
 */
export function publishRefusal(state: EditorState): string | null {
  if (state.savedText === null) return 'Nothing is saved yet. Save the draft first.';
  if (isDirty(state)) return 'Unsaved changes. Publishing uses the saved draft, so save first.';
  return null;
}

/** `revision` is a timestamp today; shown as when, since that is what it is. */
export function describeRevision(revision: string | null): string {
  if (!revision) return 'Not saved yet';
  const time = new Date(revision);
  if (Number.isNaN(time.getTime())) return `Saved · ${revision}`;
  return `Saved ${time.toLocaleString()}`;
}
